import * as React from 'react';

import _t from 'i18n!nls/admin-shared';

import { useA11yAnnouncer } from './A11yAnnouncer';

export type SortDirection = 'ASC' | 'DESC';
/**
 * Initiate a sort request for the given sort key.
 * sorti18nNoun represents the translated label to use when describing the new sort strategy to screen readers
 * */
export type HandleSortClick = (sortKey: string, sortI18nNoun: string) => void;
export type CalculateEffectOfHandleSortClick = (sortKey: string) => Sort;
type SetPage = (newPage: number) => void;
type SetPageSize = (newPageSize: number) => void;
type SetTotalRecords = (newCountOfRecords: number) => void;

interface Sort {
  direction: SortDirection;
  key: string;
}

interface Pagination {
  /** 1-based integer representing the current page */
  page: number;
  /** The count of records to return in a page */
  pageSize: number;
  totalRecords: number;
  setPage: SetPage;
  setPageSize: SetPageSize;
  setTotalRecords: SetTotalRecords;
}

const DEFAULT_INITIAL_SORT_DIRECTION: SortDirection = 'ASC';
const DEFAULT_SORT: Sort = {
  direction: DEFAULT_INITIAL_SORT_DIRECTION,
  key: 'should be overridden',
};

const DEFAULT_INITIAL_PAGE = 1;
const DEFAULT_INITIAL_PAGE_SIZE = 10;

export interface TableContextInterface {
  sort: Sort;
  handleSortClick: HandleSortClick;
  calculateEffectOfHandleSortClick: CalculateEffectOfHandleSortClick;
  pagination: Pagination;
}
const TableContext = React.createContext<TableContextInterface>({
  handleSortClick: () => undefined,
  calculateEffectOfHandleSortClick: () => DEFAULT_SORT,
  sort: DEFAULT_SORT,
  pagination: {
    page: DEFAULT_INITIAL_PAGE,
    pageSize: DEFAULT_INITIAL_PAGE_SIZE,
    totalRecords: 0,
    setPage: () => undefined,
    setPageSize: () => undefined,
    setTotalRecords: () => undefined,
  },
});

export const useSortablePaginatedTableContext = () => React.useContext(TableContext);

export interface SortablePaginatedTableContextProviderProps {
  initialSortDirection?: SortDirection;
  initialSortKey: string;
  initialPage?: number;
  initialPageSize?: number;
}

/**
 * Collection of all business logic within the table. Exported for testing only
 * @private
 */
export const usePrivateSortablePaginatedTableContextProviderLogic = (
  props: SortablePaginatedTableContextProviderProps
): TableContextInterface => {
  const { initialSortDirection, initialSortKey, initialPage, initialPageSize } = props;
  const { addAnnouncement } = useA11yAnnouncer();
  const [sortDirection, setSortDirection] = React.useState<SortDirection>(
    initialSortDirection || DEFAULT_INITIAL_SORT_DIRECTION
  );
  const [sortKey, setSortKey] = React.useState<string>(initialSortKey);
  const [page, setPage] = React.useState(initialPage || DEFAULT_INITIAL_PAGE);
  const [pageSize, setPageSize] = React.useState(initialPageSize || DEFAULT_INITIAL_PAGE_SIZE);
  const [totalRecords, setInternalTotalRecords] = React.useState(0);

  const setTotalRecords = React.useCallback<SetTotalRecords>(
    (newCountOfRecords) => {
      // Handle out-of-bounds issues that can arise when applying a filter in combination with pagination
      const necessaryPagesToDisplayNewRecordCount = Math.ceil(newCountOfRecords / pageSize);
      const currentPageIsOutOfBounds = page > necessaryPagesToDisplayNewRecordCount;
      if (currentPageIsOutOfBounds) {
        setPage(DEFAULT_INITIAL_PAGE);
      }
      setInternalTotalRecords(newCountOfRecords);
    },
    [page, pageSize]
  );

  const calculateEffectOfHandleSortClick = React.useCallback<CalculateEffectOfHandleSortClick>(
    (newSortKey) => {
      const sortingNewColumn = newSortKey !== sortKey;
      const oppositeOfCurrentSortDirection = sortDirection === 'ASC' ? 'DESC' : 'ASC';
      const newSortDirection = sortingNewColumn ? DEFAULT_INITIAL_SORT_DIRECTION : oppositeOfCurrentSortDirection;

      return {
        key: newSortKey,
        direction: newSortDirection,
      };
    },
    [sortDirection, sortKey]
  );

  const handleSortClick = React.useCallback<HandleSortClick>(
    (newSortKey) => {
      const newStateVariables = calculateEffectOfHandleSortClick(newSortKey);

      setSortKey(newStateVariables.key);
      setSortDirection(newStateVariables.direction);

      if (newStateVariables.direction === 'ASC') {
        addAnnouncement(_t('Table is now sorted in ascending order'));
      } else {
        addAnnouncement(_t('Table is now sorted in descending order'));
      }
    },
    [addAnnouncement, calculateEffectOfHandleSortClick]
  );

  const sort: Sort = {
    direction: sortDirection,
    key: sortKey,
  };

  const pagination: Pagination = {
    page,
    pageSize,
    totalRecords,
    setPage,
    setPageSize,
    setTotalRecords,
  };

  return { sort, handleSortClick, calculateEffectOfHandleSortClick, pagination };
};

export const SortablePaginatedTableContextProvider: React.LegacyFunctionComponentWithChildren<
  SortablePaginatedTableContextProviderProps
> = ({ children, ...props }) => {
  const contextProviderValue = usePrivateSortablePaginatedTableContextProviderLogic(props);

  return <TableContext.Provider value={contextProviderValue}>{children}</TableContext.Provider>;
};
